// ── 날짜 상세 모달 ────────────────────────────────────────────────────────────
// 2026-09-02 사용자 명세 재디자인: 행 테두리 제거 + 1줄 배치(진한 배지·큰 이름·우측 시간/근무지/가로 버튼),
//   인원 많을 때 내부 스크롤 (기존엔 flex 축소로 행이 짜부라져 겹쳐 보였음).
import React, { useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { STATUS_META, WEEKDAY_NAMES, styles, getTodayYmd } from './utils';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';

const { grayBtn } = styles;

// 색 배경 행 위에 얹는 흰 칩 버튼 (이미지 스펙: 흰 배경 + 얇은 외곽선)
const whiteChipBtn = {
  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 7,
  padding: '4px 9px', fontSize: 11, fontWeight: 600, color: 'var(--text-sub)',
  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
};

/**
 * 선택한 날짜의 전 직원 출퇴근 상태를 타임라인 바와 함께 표시
 * Props:
 *   day                   — { date, weekday, is_holiday, holiday_name, summary, cells, ... }
 *   onClose
 *   onEditAttendance(cell, date)
 *   onSetPlannedLocation(cell, date)
 *   onAddOverride(date)
 *   onAddOverrideForEmployee(date, userId)
 *   onEditOverride(cell, date)
 *   onPickPerson(userId, date) — (선택) 이름을 누르면 직원별 상세로(2026-10-01). 직원별 상세 탭 열쇠가 있을 때만 준다 —
 *                                없으면 이름은 예전처럼 글자.
 */
export function DayDetailModal({
  day, onClose,
  onEditAttendance, onSetPlannedLocation,
  onAddOverride, onAddOverrideForEmployee, onEditOverride,
  onPickPerson,
}) {
  useBodyScrollLock(Boolean(day));
  const [hoverUserId, setHoverUserId] = useState(null);
  const todayYmd = getTodayYmd();
  const isFuture = day.date > todayYmd;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, width: 640, maxWidth: '95vw', maxHeight: '85vh', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* 헤더 */}
        <div style={stdModalHeaderBar(12)}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>📅</div>
            <div style={{ minWidth: 0 }}>
              <div style={stdModalHeaderTitle}>
                {day.date} ({WEEKDAY_NAMES[day.weekday]})
                {day.is_holiday && <span style={{ marginLeft: 8, fontSize: 13, color: '#fda4af', fontWeight: 600 }}>🎌 {day.holiday_name}</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '2px 8px' }}>출근 {day.summary.present}</span>
                <span style={{ fontSize: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 4, padding: '2px 8px' }}>결근 {day.summary.absent}</span>
                <span style={{ fontSize: 12, background: '#dbeafe', color: '#1e40af', borderRadius: 4, padding: '2px 8px' }}>연차 {day.summary.on_leave}</span>
                {day.leave_overlap_warning && (
                  <span style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px', fontWeight: 700 }}>⚠️ 연차 겹침 {day.summary.on_leave}명</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <button onClick={() => onAddOverride(day.date)} style={{ ...grayBtn, fontSize: 12 }}>예외 등록</button>
            <button onClick={onClose} style={stdModalCloseBtn}>×</button>
          </div>
        </div>

        {/* 직원 목록 — flex:1 + minHeight:0 + 행 flexShrink:0 이어야 내부 스크롤이 생긴다
            (없으면 인원 많을 때 행들이 flex 축소로 짜부라져 겹쳐 보임) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 20px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {day.cells.map((cell) => {
            const meta  = STATUS_META[cell.status] || STATUS_META.scheduled;
            const rowBg = isFuture ? 'var(--bg-subtle)' : meta.bg;
            // 진한 단색 배지 (이미지 스펙): 상태 진한색 배경 + 흰 글씨. 예정(미래)은 회색.
            const badgeBg = (isFuture || cell.status === 'scheduled') ? 'var(--text-muted)' : meta.text;
            const badgeLabel = (cell.status === 'half_leave' && cell.half_leave_type)
              ? `반차(${cell.half_leave_type})`
              : meta.label;

            // 근무지 라벨 (실제 출근 시에만)
            const rowLocation = !isFuture && (cell.registered_ip_label || cell.check_in_address)
              ? (cell.registered_ip_label || cell.check_in_address)
              : null;

            return (
              <div
                key={cell.user_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', background: rowBg, borderRadius: 10, flexShrink: 0,
                }}
              >
                <span style={{
                  background: badgeBg, color: '#fff', borderRadius: 6, padding: '3px 8px',
                  fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {badgeLabel}
                </span>
                {/* 이름은 절대 말줄임 금지(사용자 명세) — 대신 부서·근무지가 줄어든다.
                    직원별 상세 탭을 열 수 있으면 이름을 눌러 그 사람·그 달의 상세로(2026-10-01), 올리면 파란 밑줄 */}
                {onPickPerson ? (
                  <button
                    type="button"
                    title={`${cell.name} — 직원별 상세 보기`}
                    onClick={() => onPickPerson(cell.user_id, day.date)}
                    onMouseEnter={() => setHoverUserId(cell.user_id)}
                    onMouseLeave={() => setHoverUserId(null)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit',
                      color: hoverUserId === cell.user_id ? '#2563eb' : 'var(--text)',
                      textDecoration: hoverUserId === cell.user_id ? 'underline' : 'none', textUnderlineOffset: 3,
                    }}
                  >
                    {cell.name}
                  </button>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {cell.name}
                  </span>
                )}
                {cell.department && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 2, minWidth: 0 }}>
                    {cell.department}
                  </span>
                )}
                {/* 예외 스케줄 배지 — 클릭 시 수정 모달 (과거·미래 모두 표시) */}
                {cell.has_override && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEditOverride(cell, day.date); }}
                    style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 5, padding: '1px 6px', border: '1px solid #fcd34d', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                    title="클릭하여 예외 스케줄 수정/삭제"
                  >
                    예외({cell.schedule_start}~{cell.schedule_end})
                  </button>
                )}

                <div style={{ flex: 1 }} />

                {/* 연차 유형명 — 배지와 같은 글자(예: 배지 '연차' + 유형 '연차')면 중복이라 숨기고,
                    경조·포상 등 커스텀 유형일 때만 표시 (2026-09-02 사용자 지적) */}
                {!isFuture && cell.leave_type && cell.status !== 'half_leave' && cell.leave_type !== badgeLabel && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic', whiteSpace: 'nowrap', flexShrink: 0 }}>{cell.leave_type}</span>
                )}
                {/* 위치는 시간 앞, 지각은 시간 아래 줄 (2026-09-02 사용자 명세) */}
                {rowLocation && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                    📍{rowLocation}
                  </span>
                )}
                {cell.status !== 'offday' && cell.status !== 'holiday' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', whiteSpace: 'nowrap' }}>
                      {isFuture ? (
                        cell.schedule_start
                          ? `${cell.schedule_start}${cell.schedule_end ? `-${cell.schedule_end}` : ''}`
                          : (!cell.is_workday ? '휴무' : '')
                      ) : (
                        cell.check_in
                          ? `${cell.check_in}${cell.check_out ? `-${cell.check_out}` : ''}`
                          : (cell.is_workday ? '출근 없음' : '')
                      )}
                    </span>
                    {!isFuture && cell.late_minutes > 0 && (
                      <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 700, whiteSpace: 'nowrap' }}>지각 {cell.late_minutes}분</span>
                    )}
                  </div>
                )}

                <button onClick={() => onEditAttendance(cell, day.date)} style={whiteChipBtn}>출퇴근 수정</button>
                {isFuture && cell.is_workday && (
                  <button
                    onClick={() => onSetPlannedLocation(cell, day.date)}
                    style={{ ...whiteChipBtn, color: '#1d4ed8' }}
                  >근무지 설정</button>
                )}
                <button
                  onClick={() => onAddOverrideForEmployee(day.date, cell.user_id)}
                  style={{ ...whiteChipBtn, color: '#15803d' }}
                >예외 등록</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
